// src/Components/ProjectInvoices.js
import React from 'react';
import { Link } from 'react-router-dom';
import invoiceIcon from '../assets/icons/invoice.svg';
import downloadIcon from '../assets/icons/download.svg';

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

// Optional helper for coloring payment status
const getStatusBackground = (status) => {
  switch (status) {
    case 'paid':
      return '#28a745';
    case 'needs_paid':
      return 'orange';
    case 'processed':
      return '#c376da';
    default:
      return '#555';
  }
};

/**
 * Props:
 *  - projectId (string): The current project's _id from Mongoose
 *  - invoices (array): All invoices from Mongoose (each has _id, amount, projectId, releaseId, etc.)
 *  - releases (array): All releases from Mongoose (each has _id, title, coverArt, etc.)
 *  - budget (string or number): The project's allocated budget
 *  - handleInvoiceStatusChange (fn): optional callback if you want to allow
 *      editing the status from here (signature: (invoiceId, newStatus) => void)
 */
const ProjectInvoices = ({
  projectId,
  invoices = [],
  releases = [],
  budget = '0', // might be string from Mongoose or a default
  handleInvoiceStatusChange,
}) => {
  // 1) Filter only this project's invoices
  //    AND exclude costType === 'Advance'
  const projectInvoices = invoices.filter((inv) => {
    let invProjId = inv.projectId;
    if (typeof invProjId === 'object' && invProjId !== null) {
      invProjId = invProjId._id; // if it's a populated doc
    }
    return (
      String(invProjId) === String(projectId) &&
      inv.costType !== 'Advance' // << EXCLUDE "Advance"
    );
  });

  // If no invoices for this project, show a fallback
  if (projectInvoices.length === 0) {
    return (
      <div style={{ color: 'white', marginTop: '20px' }}>
        <h2 style={{ marginBottom: '10px' }}>Project Invoices</h2>
        <p style={{ color: '#999' }}>
          No invoices found for this project (excluding any "Advance" invoices).
        </p>
      </div>
    );
  }

  // 2) Calculate total spent
  //    Now that "Advance" invoices are excluded, they're not in projectInvoices
  const totalSpent = projectInvoices.reduce((acc, inv) => {
    const parsed = parseFloat(inv.amount) || 0;
    return acc + parsed;
  }, 0);
  const totalSpentFormatted = totalSpent.toFixed(2);

  // 3) Parse the project budget
  const projectBudget = parseFloat(budget) || 0;
  const projectBudgetFormatted = projectBudget.toFixed(2);

  // 4) Group these invoices by releaseId
  const groupedByRelease = projectInvoices.reduce((acc, inv) => {
    let relId = inv.releaseId;
    if (typeof relId === 'object' && relId !== null) {
      relId = relId._id;
    }
    if (!relId) relId = 'no_release';

    if (!acc[relId]) acc[relId] = [];
    acc[relId].push(inv);
    return acc;
  }, {});

  const uniqueReleaseIds = Object.keys(groupedByRelease);

  // Helper to find a release doc by _id
  const getReleaseDoc = (releaseId) => {
    if (releaseId === 'no_release') return null; // no associated release
    return releases.find((r) => String(r._id) === String(releaseId)) || null;
  };

  // Render
  return (
    <div style={{ color: 'white', marginTop: '20px' }}>
      {/* Heading: Budget & Spent */}
      <h2 style={{ marginBottom: '20px' }}>
        Project Invoices
        <span style={{ color: '#999', fontSize: '0.9rem', marginLeft: '8px' }}>
          ( €{projectBudgetFormatted} Budget / €{totalSpentFormatted} Spent )
        </span>
      </h2>

      {/* Render each release's invoices in a block */}
      {uniqueReleaseIds.map((releaseIdKey) => {
        const releaseInvoices = groupedByRelease[releaseIdKey] || [];
        // Sort by date descending
        releaseInvoices.sort((a, b) => {
          const dateA = new Date(a.uploadDate || 0);
          const dateB = new Date(b.uploadDate || 0);
          return dateB - dateA;
        });

        // Sum this group
        const thisGroupTotal = releaseInvoices.reduce((sum, inv) => {
          const parsed = parseFloat(inv.amount) || 0;
          return sum + parsed;
        }, 0);
        const releaseGroupFormatted = thisGroupTotal.toFixed(2);

        // Release info
        const releaseDoc = getReleaseDoc(releaseIdKey);
        const releaseTitle = releaseDoc?.title || 'No Release';
        const releaseCover = releaseDoc?.coverArt || '';

        return (
          <div
            key={releaseIdKey}
            style={{
              backgroundColor: '#1e1e1e',
              padding: '20px',
              borderRadius: '10px',
              marginBottom: '30px',
            }}
          >
            {/* Release heading */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
              {releaseCover && (
                <img
                  src={releaseCover}
                  alt="Cover Art"
                  style={{
                    width: '40px',
                    height: '40px',
                    objectFit: 'cover',
                    borderRadius: '4px',
                    marginRight: '10px',
                  }}
                />
              )}
              <h3 style={{ margin: 0 }}>
                {releaseTitle}{' '}
                <span style={{ color: '#999', fontSize: '0.9rem', marginLeft: '5px' }}>
                  (Total: €{releaseGroupFormatted})
                </span>
              </h3>
            </div>

            {/* Table of invoices */}
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                tableLayout: 'fixed',
                color: 'white',
              }}
            >
              <thead>
                <tr style={{ backgroundColor: '#333' }}>
                  <th style={{ padding: '10px', textAlign: 'left', width: '30%' }}>
                    Description
                  </th>
                  <th style={{ padding: '10px', textAlign: 'left', width: '15%' }}>
                    Amount (€)
                  </th>
                  <th style={{ padding: '10px', textAlign: 'left', width: '20%' }}>
                    Cost Type
                  </th>
                  <th style={{ padding: '10px', textAlign: 'left', width: '15%' }}>
                    Upload Date
                  </th>
                  <th style={{ padding: '10px', textAlign: 'left', width: '10%' }}>
                    Status
                  </th>
                  <th style={{ padding: '10px', textAlign: 'left', width: '10%' }}>
                    Download
                  </th>
                </tr>
              </thead>
              <tbody>
                {releaseInvoices.map((inv) => {
                  const invoiceId = inv._id;
                  const uploadDateStr = inv.uploadDate
                    ? inv.uploadDate.slice(0, 10)
                    : '';

                  return (
                    <tr
                      key={invoiceId}
                      style={{
                        borderBottom: '1px solid #555',
                        backgroundColor: '#1e1e1e',
                      }}
                    >
                      {/* Description => link to InvoiceDetails */}
                      <td style={{ padding: '10px', textAlign: 'left' }}>
                        <Link
                          to={`/invoice/${invoiceId}`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            textDecoration: 'none',
                            color: 'inherit',
                          }}
                        >
                          <img
                            src={invoiceIcon}
                            alt="Invoice Icon"
                            style={{
                              width: '20px',
                              height: '20px',
                              filter: 'brightness(0) invert(1)',
                            }}
                          />
                          <strong>{inv.description}</strong>
                        </Link>
                      </td>

                      {/* Amount */}
                      <td style={{ padding: '10px', textAlign: 'left' }}>
                        € {inv.amount}
                      </td>

                      {/* Cost Type */}
                      <td style={{ padding: '10px', textAlign: 'left' }}>
                        {inv.costType || '—'}
                      </td>

                      {/* Upload Date (formatted) */}
                      <td style={{ padding: '10px', textAlign: 'left', color: 'gray' }}>
                        {uploadDateStr}
                      </td>

                      {/* Payment Status */}
                      <td style={{ padding: '10px', textAlign: 'left' }}>
                        {handleInvoiceStatusChange ? (
                          <select
                            value={inv.paymentStatus}
                            onChange={(e) => handleInvoiceStatusChange(invoiceId, e.target.value)}
                            style={{
                              padding: '5px 10px',
                              borderRadius: '5px',
                              border: 'none',
                              color: '#fff',
                              cursor: 'pointer',
                              backgroundColor: getStatusBackground(inv.paymentStatus),
                            }}
                          >
                            <option value="paid">Paid</option>
                            <option value="needs_paid">Unpaid</option>
                            <option value="processed">Processed</option>
                          </select>
                        ) : (
                          <span
                            style={{
                              padding: '5px 10px',
                              borderRadius: '5px',
                              color: '#fff',
                              backgroundColor: getStatusBackground(inv.paymentStatus),
                            }}
                          >
                            {inv.paymentStatus === 'needs_paid'
                              ? 'Unpaid'
                              : inv.paymentStatus}
                          </span>
                        )}
                      </td>

                      {/* Download */}
                      <td style={{ padding: '10px', textAlign: 'left' }}>
                        {inv.fileName ? (
                          <a
                          href={`${BACKEND_URL}/uploads/${encodeURIComponent(inv.fileName)}`}
                            download
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px',
                              textDecoration: 'none',
                              color: 'white',
                              backgroundColor: '#444',
                              padding: '5px 10px',
                              borderRadius: '5px',
                            }}
                          >
                            <img
                              src={downloadIcon}
                              alt="Download Invoice"
                              style={{
                                width: '10px',
                                height: '10px',
                                filter: 'brightness(0) invert(1)',
                              }}
                            />
                            Download
                          </a>
                        ) : (
                          <span style={{ color: '#999' }}>No File</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};

export default ProjectInvoices;